/**
 * MODULE: _shared/lib/frontend/api/workspaceSessionHandoff.ts
 * PURPOSE: Pass Traditional ↔ Agentic JWT session across separate Vite origins via URL hash
 * DEPENDENCIES: None (browser localStorage + location only)
 * PUBLIC_API: appendWorkspaceSessionHandoff, consumeWorkspaceSessionHandoff, HANDOFF_HASH_KEY
 * SIDE_EFFECTS: Reads/writes access_token + refresh_token (+ tenant_id); strips hash via replaceState
 * AGENT SAFETY: 🟢 SAFE
 * FILE_SIZE: ~165 / 200 lines
 * RECENT: 2026-07-30 [2.2.1844] — Single-login handoff between :5173 and :5174
 *
 * Hash fragments are not sent to servers. Tokens are stripped from the URL immediately on consume.
 * Max age prevents stale history entries from re-applying an old session.
 */

export const HANDOFF_HASH_KEY = 'brazn_handoff';
/** Reject handoffs older than this (browser history / shared links). */
export const HANDOFF_MAX_AGE_MS = 120_000;

const ACCESS_KEY = 'access_token';
const REFRESH_KEY = 'refresh_token';
const TENANT_KEY = 'tenant_id';

type HandoffPayloadV1 = {
  v: 1;
  access_token: string;
  refresh_token?: string;
  ts: number;
};

function looksLikeJwt(token: string): boolean {
  const parts = token.split('.');
  return parts.length === 3 && parts[0].startsWith('eyJ') && parts.every((p) => p.length > 0);
}

function readTenantIdFromAccess(accessToken: string): string | null {
  try {
    let b64 = accessToken.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? '';
    const pad = b64.length % 4;
    if (pad) b64 += '='.repeat(4 - pad);
    const claims = JSON.parse(atob(b64)) as { tenant_id?: string };
    const raw = claims.tenant_id?.trim();
    return raw ? raw.replace(/^tnt_/, '') : null;
  } catch {
    return null;
  }
}

function toBase64Url(json: string): string {
  const bytes = new TextEncoder().encode(json);
  let bin = '';
  bytes.forEach((b) => {
    bin += String.fromCharCode(b);
  });
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(b64url: string): string {
  let b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4;
  if (pad) b64 += '='.repeat(4 - pad);
  const bin = atob(b64);
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** Read current SPA session tokens (same keys Traditional + Agentic use). */
export function readWorkspaceSessionTokens(): {
  access_token: string;
  refresh_token?: string;
} | null {
  if (typeof window === 'undefined') return null;
  const access = localStorage.getItem(ACCESS_KEY)?.trim();
  if (!access || !looksLikeJwt(access)) return null;
  const refresh = localStorage.getItem(REFRESH_KEY)?.trim() || undefined;
  return { access_token: access, refresh_token: refresh };
}

export function encodeWorkspaceSessionHandoff(
  tokens: { access_token: string; refresh_token?: string },
  nowMs = Date.now(),
): string {
  const payload: HandoffPayloadV1 = {
    v: 1,
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token,
    ts: nowMs,
  };
  return `${HANDOFF_HASH_KEY}=${toBase64Url(JSON.stringify(payload))}`;
}

/**
 * Append `#brazn_handoff=…` when this origin has a session.
 * Leaves the URL unchanged when signed out (destination keeps its own session or login UX).
 */
export function appendWorkspaceSessionHandoff(url: string): string {
  const tokens = readWorkspaceSessionTokens();
  if (!tokens) return url;
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const fragment = encodeWorkspaceSessionHandoff(tokens);
    // Replace any prior handoff; keep unrelated hash keys if present (rare).
    const parts = (u.hash.startsWith('#') ? u.hash.slice(1) : u.hash)
      .split('&')
      .filter((p) => p && !p.startsWith(`${HANDOFF_HASH_KEY}=`));
    parts.push(fragment);
    u.hash = parts.join('&');
    return u.toString();
  } catch {
    const sep = url.includes('#') ? '&' : '#';
    return `${url}${sep}${encodeWorkspaceSessionHandoff(tokens)}`;
  }
}

function parseHandoffFromHash(hash: string): HandoffPayloadV1 | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;
  const param = raw.split('&').find((p) => p.startsWith(`${HANDOFF_HASH_KEY}=`));
  if (!param) return null;
  const encoded = param.slice(HANDOFF_HASH_KEY.length + 1);
  if (!encoded) return null;
  try {
    const parsed = JSON.parse(fromBase64Url(encoded)) as HandoffPayloadV1;
    if (parsed?.v !== 1 || typeof parsed.access_token !== 'string') return null;
    if (!looksLikeJwt(parsed.access_token)) return null;
    if (parsed.refresh_token != null && typeof parsed.refresh_token !== 'string') return null;
    if (typeof parsed.ts !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

function stripHandoffFromLocation(): void {
  if (typeof window === 'undefined') return;
  const { pathname, search, hash } = window.location;
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const kept = raw
    .split('&')
    .filter((p) => p && !p.startsWith(`${HANDOFF_HASH_KEY}=`))
    .join('&');
  const next = `${pathname}${search}${kept ? `#${kept}` : ''}`;
  window.history.replaceState(null, '', next);
}

/**
 * If the URL hash carries a fresh handoff, write tokens into localStorage and strip the hash.
 * @returns true when a session was applied
 */
export function consumeWorkspaceSessionHandoff(nowMs = Date.now()): boolean {
  if (typeof window === 'undefined') return false;
  const payload = parseHandoffFromHash(window.location.hash);
  if (!payload) return false;
  stripHandoffFromLocation();
  if (nowMs - payload.ts > HANDOFF_MAX_AGE_MS || nowMs - payload.ts < -60_000) {
    return false;
  }
  localStorage.setItem(ACCESS_KEY, payload.access_token);
  if (payload.refresh_token) {
    localStorage.setItem(REFRESH_KEY, payload.refresh_token);
  }
  const tenantId = readTenantIdFromAccess(payload.access_token);
  if (tenantId) localStorage.setItem(TENANT_KEY, tenantId);
  try {
    window.dispatchEvent(new CustomEvent('brazn:session-updated'));
  } catch {
    /* ignore */
  }
  return true;
}
